import React, { useState, useEffect } from 'react';
import {
  ChangeDetection,
  ChangeDetail,
  ChangeDetectionStatistics,
  MonitoringSite,
} from '../../../types/knowledgeMonitoring';
import monitoringService from '../../../services/api/knowledge/monitoringService';
import SimpleModal from '../../common/SimpleModal';
import InteractiveDiffViewer from './InteractiveDiffViewer';
import { TaskySelect } from '../../tasky';

interface ChangeDetectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  site: MonitoringSite;
}

const ChangeDetectionModal: React.FC<ChangeDetectionModalProps> = ({
  isOpen,
  onClose,
  site,
}) => {
  const [changeDetections, setChangeDetections] = useState<ChangeDetection[]>([]);
  const [statistics, setStatistics] = useState<ChangeDetectionStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedDetection, setSelectedDetection] = useState<ChangeDetection | null>(null);
  const [diffImageUrl, setDiffImageUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState<{
    has_changes_only?: boolean;
    unreviewed_only?: boolean;
    severity?: 'high' | 'medium' | 'low';
  }>({});
  const [imageCompareMode, setImageCompareMode] = useState<'interactive' | 'side-by-side' | 'overlay' | 'diff'>('interactive');

  useEffect(() => {
    if (isOpen && site) {
      loadChangeDetections();
      loadStatistics();
    }
  }, [isOpen, site, currentPage, filter]);

  const loadChangeDetections = async () => {
    setLoading(true);
    try {
      const response = await monitoringService.getChangeDetections(site.id, {
        page: currentPage,
        per_page: 10,
        ...filter,
      });
      setChangeDetections(response.data);
      setTotalPages(response.meta.last_page);
    } catch (error) {
      console.error('Failed to load change detections:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await monitoringService.getChangeDetectionStatistics(site.id);
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleViewDetails = async (detection: ChangeDetection) => {
    setSelectedDetection(detection);
    if (detection.diff_image_url) {
      try {
        const { url } = await monitoringService.getChangeDetectionDiffImage(detection.id);
        setDiffImageUrl(url);
      } catch (error) {
        console.error('Failed to load diff image:', error);
      }
    }
  };

  const handleMarkAsReviewed = async (detectionId: number, notes?: string) => {
    try {
      await monitoringService.markChangeDetectionAsReviewed(detectionId, notes);
      loadChangeDetections();
      loadStatistics();
      setSelectedDetection(null);
    } catch (error) {
      console.error('Failed to mark as reviewed:', error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return '-';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ja-JP');
  };

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'visual': return 'bg-purple/10 text-purple border-purple/20';
      case 'content': return 'bg-primary/10 text-primary border-primary/20';
      case 'structure': return 'bg-pink/10 text-pink border-pink/20';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'visual': return '視覚的変更';
      case 'content': return 'コンテンツ変更';
      case 'structure': return '構造変更';
      default: return type;
    }
  };

  const getImportanceBadge = (importance: string) => {
    switch (importance) {
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getImportanceLabel = (importance: string) => {
    switch (importance) {
      case 'high': return '高';
      case 'medium': return '中';
      case 'low': return '低';
      default: return '-';
    }
  };

  const groupChangeDetails = (details: ChangeDetail[]) => {
    const groups: Record<string, ChangeDetail[]> = {};
    details.forEach((detail) => {
      const type = detail.type || 'other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(detail);
    });
    return groups;
  };

  return (
    <SimpleModal isOpen={isOpen} onClose={onClose} title={`変更検出履歴 - ${site.title}`} size="xl">
      <div className="space-y-6 p-2">
        {/* Statistics */}
        {statistics && (
          <div className="bg-gradient-to-r from-primary/10 to-purple/10 rounded-tasky-md border border-primary/20 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">統計情報</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-sm text-gray-600 mb-1">総検出回数</div>
                <div className="text-2xl font-bold text-gray-900">{statistics.total_detections}</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-sm text-gray-600 mb-1">変更検出</div>
                <div className="text-2xl font-bold text-primary">{statistics.changes_detected}</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-sm text-gray-600 mb-1">未確認</div>
                <div className="text-2xl font-bold text-orange-600">{statistics.unreviewed_changes}</div>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="text-sm text-gray-600 mb-1">平均変更率</div>
                <div className="text-2xl font-bold text-purple">{statistics.average_diff_percentage}%</div>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-3">重要度分布</h4>
              <div className="space-y-2">
                {(['high', 'medium', 'low'] as const).map((level) => {
                  const count = statistics.severity_distribution[level] || 0;
                  const total = statistics.total_detections || 1;
                  const colors = { high: 'bg-red-500', medium: 'bg-yellow-500', low: 'bg-green-500' };
                  const labels = { high: '高', medium: '中', low: '低' };
                  return (
                    <div key={level} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-600 w-8">{labels[level]}</span>
                      <div className="flex-1 bg-gray-200 rounded-full h-8 overflow-hidden">
                        <div
                          className={`${colors[level]} h-full flex items-center justify-end pr-2 text-white text-sm font-medium transition-all duration-300`}
                          style={{ width: `${(count / total) * 100}%` }}
                        >
                          {count > 0 && count}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">フィルター</h3>
          <div className="flex flex-wrap gap-2">
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter.has_changes_only === true ? 'bg-primary text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              onClick={() => setFilter({ ...filter, has_changes_only: filter.has_changes_only === true ? undefined : true })}
            >
              変更あり
            </button>
            <button
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${filter.unreviewed_only === true ? 'bg-primary text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              onClick={() => setFilter({ ...filter, unreviewed_only: filter.unreviewed_only === true ? undefined : true })}
            >
              未確認
            </button>
            <TaskySelect
              value={filter.severity || ''}
              onChange={(e) => setFilter({ ...filter, severity: e.target.value as any || undefined })}
              className="h-9 text-sm"
            >
              <option value="">重要度: すべて</option>
              <option value="high">重要度: 高</option>
              <option value="medium">重要度: 中</option>
              <option value="low">重要度: 低</option>
            </TaskySelect>
            <button
              className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              onClick={() => { setFilter({}); setCurrentPage(1); }}
            >
              リセット
            </button>
          </div>
        </div>

        {/* Detection list */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">検出履歴</h3>
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500">読み込み中...</div>
          ) : changeDetections.length === 0 ? (
            <div className="p-8 text-center text-gray-500">変更検出履歴がありません</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {changeDetections.map((detection) => (
                <div
                  key={detection.id}
                  className={`p-6 hover:bg-gray-50 transition-colors cursor-pointer ${detection.reviewed_at ? 'bg-gray-50/50' : ''}`}
                  onClick={() => handleViewDetails(detection)}
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-gray-900">{formatDate(detection.detected_at)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(detection.severity_level)}`}>
                          {getSeverityLabel(detection.severity_level)}
                        </span>
                        {detection.reviewed_at && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">確認済み</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        変更率: <span className="font-medium text-gray-700">{(detection.visual_diff_percentage ?? 0).toFixed(2)}%</span>
                      </div>
                    </div>
                  </div>

                  {detection.change_summary && (
                    <div className="mb-4 bg-primary/5 rounded-tasky-sm p-4 border border-primary/20">
                      <h5 className="text-xs font-semibold text-dark mb-2">変更サマリー</h5>
                      <p className="text-xs text-gray-700 leading-relaxed">{detection.change_summary}</p>
                    </div>
                  )}

                  {detection.change_details && detection.change_details.length > 0 && (
                    <div className="mb-4 bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="text-xs font-semibold text-gray-900 mb-3">変更詳細 ({detection.change_details.length}件)</h5>
                      <div className="space-y-2">
                        {detection.change_details.slice(0, 3).map((detail: any, index: number) => (
                          <div key={index} className="flex items-start gap-2 text-xs">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 border ${getChangeTypeColor(detail.type)}`}>
                              {getChangeTypeLabel(detail.type)}
                            </span>
                            <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${getImportanceBadge(detail.importance)}`}>
                              {getImportanceLabel(detail.importance)}
                            </span>
                            <span className="text-gray-700 flex-1 line-clamp-2">{detail.description}</span>
                          </div>
                        ))}
                        {detection.change_details.length > 3 && (
                          <div className="text-xs text-primary font-medium">+ {detection.change_details.length - 3}件の変更</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 border-t border-gray-200">
                    <button
                      className="px-3 py-1.5 bg-primary hover:bg-primary-hover text-white rounded-md text-sm font-medium transition-colors shadow-sm"
                      onClick={(e) => { e.stopPropagation(); handleViewDetails(detection); }}
                    >
                      詳細を表示
                    </button>
                    {!detection.reviewed_at && (
                      <button
                        className="px-3 py-1.5 bg-success hover:bg-success/90 text-white rounded-md text-sm font-medium transition-colors shadow-sm"
                        onClick={(e) => { e.stopPropagation(); handleMarkAsReviewed(detection.id); }}
                      >
                        確認済みにする
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-center gap-2">
            <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 bg-white border border-warm-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              前へ
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                const showPage = pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1);
                const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
                const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < totalPages - 2;
                if (showEllipsisBefore || showEllipsisAfter) return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                if (!showPage) return null;
                return (
                  <button key={pageNum} onClick={() => setCurrentPage(pageNum)}
                    className={`min-w-[32px] h-8 px-2 rounded-md text-sm font-medium transition-colors ${currentPage === pageNum ? 'bg-primary text-white' : 'bg-white border border-warm-border text-gray-700 hover:bg-gray-50'}`}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1.5 bg-white border border-warm-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              次へ
            </button>
          </div>
        </div>

        {/* Detail modal */}
        {selectedDetection && (
          <SimpleModal isOpen={!!selectedDetection} onClose={() => { setSelectedDetection(null); setDiffImageUrl(null); }} title="変更検出の詳細" size="lg">
            <div className="space-y-6 max-h-[70vh] overflow-y-auto px-2">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary/10 to-purple/10 rounded-tasky-md p-5 border border-primary/20">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900">{formatDate(selectedDetection.detected_at)}</h4>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getSeverityColor(selectedDetection.severity_level)}`}>
                    重要度: {getSeverityLabel(selectedDetection.severity_level)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="text-xs text-gray-600 mb-1">視覚的変更率</div>
                    <div className="text-xl font-bold text-primary">{(selectedDetection.visual_diff_percentage ?? 0).toFixed(2)}%</div>
                  </div>
                  {selectedDetection.content_similarity_percentage !== null && (
                    <div className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="text-xs text-gray-600 mb-1">コンテンツ類似度</div>
                      <div className="text-xl font-bold text-purple">{(selectedDetection.content_similarity_percentage ?? 0).toFixed(2)}%</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Before/After Image Comparison */}
              {(selectedDetection.previous_snapshot?.screenshot_url || selectedDetection.current_snapshot?.screenshot_url) && (
                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <span className="w-1 h-5 bg-pink-600 rounded-full"></span>
                      画像比較
                    </h5>
                    <div className="flex gap-1">
                      {(['interactive', 'side-by-side', 'overlay', 'diff'] as const).map((mode) => (
                        <button key={mode} onClick={() => setImageCompareMode(mode)}
                          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${imageCompareMode === mode ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                          {mode === 'interactive' ? 'インタラクティブ' : mode === 'side-by-side' ? '並列' : mode === 'overlay' ? '重ね合わせ' : '差分'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {imageCompareMode === 'interactive' && (
                    <InteractiveDiffViewer
                      currentImageUrl={selectedDetection.current_snapshot?.screenshot_url || ''}
                      previousImageUrl={selectedDetection.previous_snapshot?.screenshot_url}
                      diffImageUrl={selectedDetection.diff_image_url || undefined}
                      changeDetails={selectedDetection.change_details || []}
                      changedRegions={(selectedDetection.changed_regions || []).filter(
                        (region) => region.x || region.y || region.width || region.height
                      )}
                      imageWidth={1280}
                      imageHeight={800}
                    />
                  )}

                  {imageCompareMode === 'side-by-side' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2 text-center">前回 (Before)</div>
                        {selectedDetection.previous_snapshot?.screenshot_url ? (
                          <img src={selectedDetection.previous_snapshot.screenshot_url} alt="Before" className="w-full rounded-lg border border-gray-200 shadow-sm" />
                        ) : (
                          <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">画像なし</div>
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-medium text-gray-500 mb-2 text-center">今回 (After)</div>
                        {selectedDetection.current_snapshot?.screenshot_url ? (
                          <img src={selectedDetection.current_snapshot.screenshot_url} alt="After" className="w-full rounded-lg border border-gray-200 shadow-sm" />
                        ) : (
                          <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">画像なし</div>
                        )}
                      </div>
                    </div>
                  )}

                  {imageCompareMode === 'diff' && selectedDetection.diff_image_url && (
                    <div className="text-center">
                      <div className="text-xs font-medium text-gray-500 mb-2">差分画像（赤枠: 変更箇所）</div>
                      <img src={selectedDetection.diff_image_url} alt="Diff" className="max-w-full rounded-lg border-2 border-red-200 shadow-sm mx-auto" />
                    </div>
                  )}

                  {imageCompareMode === 'diff' && !selectedDetection.diff_image_url && (
                    <div className="text-center py-8 text-gray-400 text-sm">差分画像が生成されていません</div>
                  )}

                  {imageCompareMode === 'overlay' && (
                    <div className="relative">
                      <div className="text-xs font-medium text-gray-500 mb-2 text-center">重ね合わせ表示</div>
                      <div className="relative">
                        {selectedDetection.current_snapshot?.screenshot_url && (
                          <img src={selectedDetection.current_snapshot.screenshot_url} alt="After" className="w-full rounded-lg border border-gray-200" />
                        )}
                        {selectedDetection.previous_snapshot?.screenshot_url && (
                          <img src={selectedDetection.previous_snapshot.screenshot_url} alt="Before" className="absolute top-0 left-0 w-full rounded-lg opacity-40" style={{ mixBlendMode: 'difference' }} />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Change Summary */}
              {selectedDetection.change_summary && (
                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-1 h-5 bg-primary rounded-full"></span>変更概要
                  </h5>
                  <p className="text-sm text-gray-700 leading-relaxed">{selectedDetection.change_summary}</p>
                </div>
              )}

              {/* Change Details grouped by type */}
              {selectedDetection.change_details && selectedDetection.change_details.length > 0 && (
                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                  <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-purple rounded-full"></span>変更詳細 ({selectedDetection.change_details.length}件)
                  </h5>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(groupChangeDetails(selectedDetection.change_details)).map(([type, details]) => (
                      <span key={type} className={`px-3 py-1 rounded-full text-xs font-medium border ${getChangeTypeColor(type)}`}>
                        {getChangeTypeLabel(type)}: {details.length}件
                      </span>
                    ))}
                  </div>
                  <div className="space-y-3">
                    {selectedDetection.change_details.map((detail, index) => (
                      <div key={index} className={`rounded-lg p-4 border hover:shadow-md transition-shadow ${detail.importance === 'high' ? 'bg-red-50 border-red-200' : detail.importance === 'medium' ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getChangeTypeColor(detail.type)}`}>{getChangeTypeLabel(detail.type)}</span>
                            <span className={`px-2 py-1 rounded-md text-xs font-semibold ${getImportanceBadge(detail.importance)}`}>{getImportanceLabel(detail.importance)}</span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed mb-2">{detail.description}</p>
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{detail.location}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Overall Impact */}
              {selectedDetection.overall_impact && (
                <div className="bg-amber-50 rounded-lg p-5 shadow-sm border border-amber-200">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    総合的な影響
                  </h5>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedDetection.overall_impact}</p>
                </div>
              )}

              {/* AI Recommendations */}
              {selectedDetection.ai_recommendations && (
                <div className="bg-green-50 rounded-lg p-5 shadow-sm border border-green-200">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    AI推奨事項
                  </h5>
                  <div className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">{selectedDetection.ai_recommendations}</div>
                </div>
              )}

              {/* Changed Regions */}
              {selectedDetection.changed_regions && (() => {
                const validRegions = selectedDetection.changed_regions.filter((region: any) => region.x || region.y || region.width || region.height);
                return validRegions.length > 0 && (
                  <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                    <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <span className="w-1 h-5 bg-primary rounded-full"></span>変更領域 ({validRegions.length}箇所)
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {validRegions.map((region, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-xs">
                          <div className="flex items-center justify-between mb-2">
                            <span className="px-2 py-0.5 bg-primary/10 text-primary rounded font-medium">{region.change_type}</span>
                            <span className="font-semibold text-primary">{(region.diff_percentage ?? 0).toFixed(2)}%</span>
                          </div>
                          <div className="text-gray-600">位置: ({region.x}, {region.y}) | サイズ: {region.width}x{region.height}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Diff image standalone */}
              {selectedDetection.diff_image_url && !selectedDetection.previous_snapshot?.screenshot_url && (
                <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-200">
                  <h5 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 bg-pink-600 rounded-full"></span>差分画像
                  </h5>
                  <a href={selectedDetection.diff_image_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors shadow-sm">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    差分画像を表示
                  </a>
                </div>
              )}

              {/* Actions */}
              {!selectedDetection.reviewed_at && (
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    className="px-6 py-2.5 bg-success hover:bg-success/90 text-white rounded-lg font-medium transition-colors shadow-sm"
                    onClick={() => {
                      const notes = prompt('確認メモを入力してください（任意）:');
                      handleMarkAsReviewed(selectedDetection.id, notes || undefined);
                    }}
                  >
                    確認済みにする
                  </button>
                </div>
              )}
            </div>
          </SimpleModal>
        )}
      </div>
    </SimpleModal>
  );
};

export default ChangeDetectionModal;
